import { useInfiniteQuery } from '@tanstack/react-query';
import { ShieldCheck } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router';
import { api, qs } from '../../api/client';
import { useIsAdmin } from '../../api/session';
import type { ActorRef } from '../../api/types';
import { useCrumbs } from '../../layout/crumbs';
import { StateBadge, humanize } from '../../ui/Badge';
import { EmptyState, QueryView } from '../../ui/feedback';
import { Select } from '../../ui/forms';
import { ActorName, LoadMore, PageHeader, Tabs, usePageTitle } from '../../ui/misc';
import { Timestamp } from '../../ui/Timestamp';

interface ApprovalRow {
  id: string;
  displayId: string;
  intent: string;
  state: string;
  project: { key: string; name: string };
  target: string;
  requester: ActorRef;
  origin: string;
  decidedBy: ActorRef | null;
  createdAt: string;
  expiresAt: string | null;
}

/** WF-05/06/12/16: acceptance, release and rollback requests in one queue. */
export function ApprovalsPage() {
  usePageTitle('Approvals');
  useCrumbs([{ label: 'Workspace' }, { label: 'Approvals' }]);
  const admin = useIsAdmin();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const state = params.get('state') ?? (admin ? 'pending' : '');
  const intent = params.get('intent') ?? '';
  const q = useInfiniteQuery({
    queryKey: ['approvals', state, intent],
    queryFn: ({ pageParam }) =>
      api.get<{ items: ApprovalRow[]; nextCursor: string | null }>(
        `/approvals${qs({ state, intent, cursor: pageParam })}`,
      ),
    initialPageParam: '',
    getNextPageParam: (l) => l.nextCursor ?? undefined,
  });
  const set = (k: string, v: string) => {
    const n = new URLSearchParams(params);
    n.set(k, v);
    setParams(n, { replace: true });
  };
  return (
    <div className="page">
      <PageHeader
        title="Approvals"
        subtitle={
          admin
            ? 'Decide pending requests. Opening a request never approves it.'
            : 'Requests for projects you can access. Only admins decide.'
        }
      />
      <div className="row between">
        <Tabs
          label="Approval state"
          value={state || 'all'}
          onChange={(v) => set('state', v === 'all' ? '' : v)}
          tabs={[
            { value: 'pending', label: 'Pending' },
            { value: 'approved', label: 'Approved' },
            { value: 'all', label: 'All' },
          ]}
        />
        <Select
          aria-label="Intent"
          value={intent}
          onChange={(e) => set('intent', e.target.value)}
          style={{ width: 200 }}
        >
          <option value="">All intents</option>
          <option value="acceptance">Acceptance</option>
          <option value="release">Release</option>
          <option value="rollback">Rollback</option>
        </Select>
      </div>
      <QueryView query={q}>
        {(d) => {
          const items = d.pages.flatMap((p) => p.items);
          if (!items.length) return <EmptyState icon={ShieldCheck} title="No approval requests" />;
          return (
            <>
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Request</th>
                      <th>Intent</th>
                      <th>Target</th>
                      <th>Requester</th>
                      <th>State</th>
                      <th>Expires</th>
                      <th>Decided by</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((a) => (
                      <tr
                        key={a.id}
                        className="clickable"
                        onClick={() => navigate(`/approvals/${a.displayId}`)}
                      >
                        <td>
                          <a
                            href={`/approvals/${a.displayId}`}
                            onClick={(e) => {
                              e.preventDefault();
                              navigate(`/approvals/${a.displayId}`);
                            }}
                            className="mono"
                          >
                            {a.displayId}
                          </a>
                        </td>
                        <td>{humanize(a.intent)}</td>
                        <td>
                          <span className="mono small muted">{a.project.key}</span> {a.target}
                        </td>
                        <td className="small">
                          <ActorName actor={a.requester} />
                          {(a.origin === 'agent' || a.origin === 'mcp') && (
                            <span className="muted"> via agent</span>
                          )}
                        </td>
                        <td>
                          <StateBadge state={a.state} />
                        </td>
                        <td className="small">
                          <Timestamp value={a.expiresAt} />
                        </td>
                        <td className="small">
                          <ActorName actor={a.decidedBy} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <LoadMore
                hasMore={!!q.hasNextPage}
                onClick={() => q.fetchNextPage()}
                loading={q.isFetchingNextPage}
              />
            </>
          );
        }}
      </QueryView>
    </div>
  );
}
